import React, { useEffect } from 'react';
import redirectTo from '@99designs/apps/spa/common/Ssr/redirectTo';
import useWorkflowMutations, {
    LaunchState,
} from '@99designs/apps/spa/pages/create/util/useWorkflowMutations';
import { stripParamFromUrl } from '@99designs/browser';
import { BriefState } from '@99designs/graph-utils/types';
import { useViewer } from '@99designs/viewer';
import { LaunchBriefMutation } from '../briefs/Brief.generated';
import { PARTNER_TRANSITION_PARAM } from '../common/components/ExternalBriefTransition';

interface LaunchBriefProps {
    brief: {
        id: string;
        state: BriefState;
        userId: number;
    };
    children: (props: { loading: boolean; data: LaunchBriefMutation | null }) => React.ReactElement;
}

// Authenticate, Claim, and Launch all wrapped up into one.
export default function LaunchBrief({ brief, children }: LaunchBriefProps) {
    const viewer = useViewer();
    const [launchState, launch, claim, data] = useWorkflowMutations({
        id: brief.id,
        state: brief.state,
        userId: brief.userId,
    });

    useEffect(
        () => {
            (async function () {
                if (!viewer) {
                    redirectTo('/signup', [
                        { key: 'source', value: 'projects_launch' },
                        { key: 'role', value: 'customer' },
                        { key: 'skip-transition', value: 'true' },
                        // post login we should not show the partner transition UI again, so the `fromPartner` param must be removed from the target
                        {
                            key: 'target',
                            value: stripParamFromUrl(
                                window.location.href,
                                PARTNER_TRANSITION_PARAM
                            ),
                        },
                    ]);
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    return <></>;
                }

                await claim();
                await launch({ delay: 0 });
            })();
        },
        // claim and launch both hold state however we don't want to attempt multiple launches
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [viewer]
    );

    if (launchState === LaunchState.Error) {
        throw new Error('There has been an issue launching your project. Please contact support.');
    }

    // eslint-disable-next-line eqeqeq
    return children({ loading: launchState == LaunchState.InProgress, data });
}
